import { useEffect } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bot, Sparkles, Trash2, Share2 } from "lucide-react";
import type { Agent } from "@/types/aiToolkit";
import {
  AgentBuildForm,
  defaultAgentPartial,
  validateAgentBuild,
} from "@/components/agents/AgentBuildForm";

interface AgentBuilderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent?: Agent | null;
  defaultFolderId?: string | null;
  onSave: (agent: Partial<Agent>) => void;
  onDelete?: (agentId: string) => void;
  onShare?: (agent: Agent) => void;
}

export function AgentBuilderModal({
  open,
  onOpenChange,
  agent,
  defaultFolderId,
  onSave,
  onDelete,
  onShare,
}: AgentBuilderModalProps) {
  const [formData, setFormData] = useState<Partial<Agent>>(defaultAgentPartial);

  useEffect(() => {
    if (agent) {
      setFormData({
        ...defaultAgentPartial,
        ...agent,
        persona: agent.persona || defaultAgentPartial.persona,
        outputRequirements: agent.outputRequirements || defaultAgentPartial.outputRequirements,
        linkedSourceIds: agent.linkedSourceIds ?? [],
        trainingDocumentIds: agent.trainingDocumentIds ?? [],
      });
    } else {
      setFormData({
        ...defaultAgentPartial,
        id: `custom-agent-${Date.now()}`,
        createdAt: new Date().toISOString(),
        createdBy: "user",
        folderId: defaultFolderId ?? null,
      });
    }
  }, [agent, open, defaultFolderId]);

  const validation = validateAgentBuild(formData);

  const handleSave = () => {
    if (!validation.valid) return;
    onSave(formData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col gap-4">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            {agent ? "Edit Agent" : "Create New Agent"}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden flex flex-col">
          <AgentBuildForm
            value={formData}
            onChange={setFormData}
            defaultFolderId={defaultFolderId}
            showFolderPicker
            fillContainer
          />
        </div>

        <DialogFooter className="shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2">
              {agent && agent.createdBy === "user" && onShare && (
                <Button
                  variant="outline"
                  onClick={() => onShare(agent)}
                  data-testid="button-share-agent-modal"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
              {agent && agent.createdBy === "user" && onDelete && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (
                      confirm(
                        `Are you sure you want to delete "${agent.name}"? This action cannot be undone.`
                      )
                    ) {
                      onDelete(agent.id);
                      onOpenChange(false);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!validation.valid || !formData.name?.trim()}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {agent ? "Save Changes" : "Create Agent"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
